import React from 'react';
import {View, TouchableOpacity, StyleSheet, ActivityIndicator} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import {MaterialCommunityIcons} from '@expo/vector-icons';

const TEAL = '#3DAB9E';

interface PuzzleRoyaleBannerProps {
  /** Disable + show a spinner while the join is in flight. */
  joining?: boolean;
  onJoin: () => void;
  /** Dismiss the banner (like the classroom test banner's close affordance). */
  onClose: () => void;
}

/**
 * Student-facing banner shown when a coach has created a Puzzle Royale in the
 * classroom. Tapping Join auto-joins the latest royale (by share code) and
 * opens it in a new tab. Mirrors MiniTournamentBanner but in royale teal and
 * with no countdown (royales start when the coach starts / the lobby fills).
 */
export default function PuzzleRoyaleBanner({
  joining,
  onJoin,
  onClose,
}: PuzzleRoyaleBannerProps) {
  return (
    <View style={styles.banner}>
      <MaterialCommunityIcons
        name="puzzle-outline"
        size={20}
        color={CCColors.White}
      />
      <View style={styles.textCol}>
        <CCText fontSize={FontSizes[14]} fontWeight={600} color={CCColors.White}>
          Puzzle Royale is live!
        </CCText>
        <CCText fontSize={FontSizes[12]} color={CCColors.White}>
          Race your classmates to solve the most puzzles.
        </CCText>
      </View>
      <TouchableOpacity
        onPress={onJoin}
        disabled={joining}
        style={[styles.joinBtn, joining && styles.joinBtnDisabled]}>
        {joining ? (
          <ActivityIndicator size="small" color={TEAL} />
        ) : (
          <CCText fontSize={FontSizes[13]} fontWeight={600} color={TEAL}>
            Join Puzzle Royale
          </CCText>
        )}
      </TouchableOpacity>
      <TouchableOpacity
        onPress={onClose}
        style={styles.closeBtn}
        accessibilityLabel="Dismiss puzzle royale banner"
        hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}>
        <MaterialCommunityIcons name="close" size={20} color={CCColors.White} />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: TEAL,
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginHorizontal: 12,
    marginTop: 8,
    borderRadius: 8,
  },
  textCol: {
    flex: 1,
    flexDirection: 'column',
  },
  joinBtn: {
    backgroundColor: CCColors.White,
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
    minWidth: 130,
    alignItems: 'center',
    justifyContent: 'center',
  },
  joinBtnDisabled: {
    opacity: 0.7,
  },
  closeBtn: {
    padding: 4,
    marginLeft: 4,
  },
});
